import { Link } from 'react-router-dom'
import {
  Shield,
  Zap,
  BarChart3,
  CheckCircle,
  Users,
  ArrowRight,
} from 'lucide-react'
import Footer from '../components/layout/Footer'

const Landing = () => {
  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Защита от блокировок',
      description: 'Автоматически находим риски в описаниях товаров до публикации',
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Экономия времени',
      description: 'Проверка за секунды вместо часов ручного анализа',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Детальная аналитика',
      description: 'Отчёты с конкретными рекомендациями по исправлению',
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Соответствие оферте',
      description: 'Сравнение с актуальной офертой Wildberries через RAG + LLM',
    },
  ]

  const steps = [
    {
      number: '1',
      title: 'Вставьте описание',
      description: 'Скопируйте текст карточки товара или загрузите CSV',
    },
    {
      number: '2',
      title: 'ИИ анализирует риски',
      description: 'Наша модель проверяет текст по 100+ пунктам оферты',
    },
    {
      number: '3',
      title: 'Получите отчёт',
      description: 'Конкретные рекомендации с ссылками на пункты оферты',
    },
    {
      number: '4',
      title: 'Исправьте и публикуйте',
      description: 'Безопасная публикация без страха блокировки',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Навигация */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">WB</span>
              </div>
              <span className="ml-2 text-xl font-bold text-gray-900">Greenlight</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-700 hover:text-primary-600">
                Возможности
              </a>
              <a href="#how-it-works" className="text-gray-700 hover:text-primary-600">
                Как это работает
              </a>
            </nav>
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="btn btn-primary"
              >
                Попробовать бесплатно
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* Герой-секция */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 tracking-tight">
                Избегайте блокировок на{' '}
                <span className="text-primary-600">Wildberries</span> с помощью ИИ
              </h1>
              <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
                Вставьте API-токен продавца и проверяйте карточки без ограничений — без регистрации.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center px-8 py-3 btn btn-primary text-lg"
                >
                  Начать проверку
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
              <div className="mt-12">
                <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-700">
                  <Users className="w-4 h-4 mr-2" />
                  <span>Более 500 селлеров уже защищают свои карточки</span>
                </div>
              </div>
            </div>

            {/* Скриншот/иллюстрация */}
            <div className="mt-16 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-purple-500 opacity-10 blur-3xl rounded-full" />
              <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 p-2">
                <div className="bg-gray-900 rounded-xl p-4 flex items-center">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="mx-auto text-gray-400 text-sm">
                    dashboard.wb-greenlight.ru
                  </div>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-5/6" />
                    </div>
                    <div className="space-y-4">
                      <div className="h-20 bg-success-50 border border-success-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-success-500 mr-2" />
                          <span className="font-medium text-success-700">0 рисков</span>
                        </div>
                      </div>
                      <div className="h-20 bg-danger-50 border border-danger-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <Shield className="w-5 h-5 text-danger-500 mr-2" />
                          <span className="font-medium text-danger-700">2 критические ошибки</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="h-32 bg-gray-50 rounded-lg border border-gray-200" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Возможности */}
        <section id="features" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">Почему выбирают WB Greenlight</h2>
              <p className="mt-4 text-lg text-gray-600">
                Современные технологии для защиты вашего бизнеса на Wildberries
              </p>
            </div>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, idx) => (
                <div key={idx} className="card hover:shadow-lg transition-shadow">
                  <div className="text-primary-600 mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                  <p className="mt-2 text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Как это работает */}
        <section id="how-it-works" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">Как это работает</h2>
              <p className="mt-4 text-lg text-gray-600">
                Всего 4 простых шага к безопасным карточкам товаров
              </p>
            </div>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step) => (
                <div key={step.number} className="relative">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="card pt-10">
                    <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                    <p className="mt-2 text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-primary-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white">
              Готовы защитить свои карточки?
            </h2>
            <p className="mt-4 text-xl text-primary-100">
              Проверки без ограничений — вставьте токен и начните прямо сейчас
            </p>
            <div className="mt-8">
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition"
              >
                Начать проверку
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            <p className="mt-4 text-sm text-primary-200">
              Продолжая, вы соглашаетесь с{' '}
              <Link to="/offer" className="underline">
                офертой
              </Link>{' '}
              и{' '}
              <Link to="/privacy" className="underline">
                политикой конфиденциальности
              </Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Landing